import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Divider,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Paper,
  Chip,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Launch as LaunchIcon,
  QrCode as QrCodeIcon,
  Person as PersonIcon,
  UploadFile as UploadFileIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { QRCodeSVG } from 'qrcode.react';

interface LaunchPanelProps {
  campaignId: string;
  organizationId: string;
  isLaunched: boolean;
}

interface Invitee {
  email: string;
  firstName?: string;
  lastName?: string;
}

const LaunchPanel: React.FC<LaunchPanelProps> = ({ 
  campaignId, 
  organizationId, 
  isLaunched = false 
}) => {
  const [expanded, setExpanded] = useState<boolean>(true);
  const [accessType, setAccessType] = useState<'open' | 'invite'>('open');
  const [confirmDialogOpen, setConfirmDialogOpen] = useState<boolean>(false);
  const [invitees, setInvitees] = useState<Invitee[]>([]);
  const [newInvitee, setNewInvitee] = useState<Invitee>({ email: '', firstName: '', lastName: '' });
  const [fileUploadError, setFileUploadError] = useState<string | null>(null);
  
  // Mock data for subscription limits
  const subscriptionLimit = 50;

  const applicationUrl = `https://apply.mentoringfusion.com/${campaignId}`;

  const handleAccessTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAccessType((event.target as HTMLInputElement).value as 'open' | 'invite');
  };

  const handleExpandChange = () => {
    setExpanded(!expanded);
  };

  const handleLaunchClick = () => {
    setConfirmDialogOpen(true);
  };

  const handleConfirmLaunch = () => {
    // API call to launch the campaign would go here
    console.log('Launching campaign...');
    setConfirmDialogOpen(false);
    // Ideally, we'd update isLaunched state from parent component
  };

  const handleAddInvitee = () => {
    if (!newInvitee.email || !newInvitee.email.includes('@')) {
      return;
    }
    
    if (invitees.length >= subscriptionLimit) {
      alert(`You've reached your subscription limit of ${subscriptionLimit} invitees.`);
      return;
    }

    setInvitees([...invitees, newInvitee]);
    setNewInvitee({ email: '', firstName: '', lastName: '' });
  };

  const handleRemoveInvitee = (index: number) => {
    const newInvitees = [...invitees];
    newInvitees.splice(index, 1);
    setInvitees(newInvitees);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    setFileUploadError(null);
    
    if (!file) return;

    if (file.type !== 'text/csv' && file.type !== 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
      setFileUploadError('Please upload a CSV or XLSX file');
      return;
    }

    // Mock file processing - in a real application, you'd process the file here
    // For CSV, you could use Papa Parse
    // For XLSX, you could use SheetJS
    console.log('Processing file:', file.name);
    
    // Mock successful upload
    const mockData: Invitee[] = [
      { email: 'example1@example.com', firstName: 'John', lastName: 'Doe' },
      { email: 'example2@example.com', firstName: 'Jane', lastName: 'Smith' }
    ];
    
    // Check subscription limit
    if (invitees.length + mockData.length > subscriptionLimit) {
      setFileUploadError(`Adding these ${mockData.length} invitees would exceed your subscription limit of ${subscriptionLimit}`);
      return;
    }
    
    setInvitees([...invitees, ...mockData]);
  };

  return (
    <Accordion 
      expanded={expanded} 
      onChange={handleExpandChange}
      sx={{ 
        mt: 2, 
        mb: 3,
        borderRadius: 2,
        overflow: 'hidden',
        '&:before': {
          display: 'none',
        },
        boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.05)'
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls="launch-panel-content"
        id="launch-panel-header"
        sx={{
          backgroundColor: 'background.paper',
          borderBottom: expanded ? '1px solid' : 'none',
          borderColor: 'divider'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <LaunchIcon sx={{ mr: 1.5, color: 'primary.main' }} />
          <Typography variant="h6" sx={{ fontWeight: 500 }}>
            Launch Application
          </Typography>
          {isLaunched && (
            <Chip
              icon={<LockIcon />}
              label="Launched"
              color="success"
              size="small"
              sx={{ ml: 2 }}
            />
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 3, backgroundColor: 'background.paper' }}>
        {isLaunched ? (
          <Box>
            <Alert severity="info" sx={{ mb: 3 }}>
              This application has been launched and is now available to applicants. The application form is locked and cannot be modified.
            </Alert>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Application URL</Typography>
                    <TextField 
                      fullWidth
                      variant="outlined"
                      value={applicationUrl}
                      InputProps={{ readOnly: true }}
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                      Share this URL with applicants or use the QR code.
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom align="center">QR Code</Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                      <QRCodeSVG value={applicationUrl} size={180} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            
              {accessType === 'invite' && (
                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6">Manage Invitees</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {invitees.length} of {subscriptionLimit} licenses used
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 3 }}>
                        <TextField
                          label="Email"
                          value={newInvitee.email}
                          onChange={(e) => setNewInvitee({...newInvitee, email: e.target.value})}
                          sx={{ mr: 1, flexGrow: 1 }}
                          size="small"
                        />
                        <TextField
                          label="First Name (optional)"
                          value={newInvitee.firstName}
                          onChange={(e) => setNewInvitee({...newInvitee, firstName: e.target.value})}
                          sx={{ mr: 1 }}
                          size="small"
                        />
                        <TextField
                          label="Last Name (optional)"
                          value={newInvitee.lastName}
                          onChange={(e) => setNewInvitee({...newInvitee, lastName: e.target.value})}
                          sx={{ mr: 1 }}
                          size="small"
                        />
                        <Button 
                          variant="contained" 
                          startIcon={<AddIcon />}
                          onClick={handleAddInvitee}
                          disabled={!newInvitee.email || invitees.length >= subscriptionLimit}
                        >
                          Add
                        </Button>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Button
                          component="label"
                          variant="outlined"
                          startIcon={<UploadFileIcon />}
                          sx={{ mr: 2 }}
                        >
                          Upload CSV/XLSX
                          <input
                            type="file"
                            hidden
                            accept=".csv,.xlsx"
                            onChange={handleFileUpload}
                          />
                        </Button>
                        <Typography variant="body2" color="text.secondary">
                          Format: "email", "first_name", "last_name" (headers required)
                        </Typography>
                      </Box>
                      
                      {fileUploadError && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                          {fileUploadError}
                        </Alert>
                      )}

                      {invitees.length > 0 && (
                        <Paper variant="outlined" sx={{ maxHeight: 300, overflow: 'auto' }}>
                          <List dense>
                            {invitees.map((invitee, index) => (
                              <ListItem
                                key={index}
                                secondaryAction={
                                  <IconButton edge="end" aria-label="delete" onClick={() => handleRemoveInvitee(index)}>
                                    <DeleteIcon />
                                  </IconButton>
                                }
                              >
                                <ListItemText
                                  primary={invitee.email}
                                  secondary={invitee.firstName || invitee.lastName ? `${invitee.firstName || ''} ${invitee.lastName || ''}`.trim() : undefined}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Paper>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>
          </Box>
        ) : (
          <Box>
            <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 3 }}>
              <Box>
                <Typography variant="subtitle1" fontWeight="bold">Important Notice</Typography>
                <Typography variant="body2">
                  Once launched, the application form will be locked and cannot be modified. 
                  Make sure your application questions and settings are finalized before launching.
                </Typography>
              </Box>
            </Alert>
            
            <Card variant="outlined" sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Access Control</Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Choose how applicants will access your application form:
                </Typography>
                
                <FormControl component="fieldset">
                  <RadioGroup
                    aria-label="application-access"
                    name="application-access"
                    value={accessType}
                    onChange={handleAccessTypeChange}
                  >
                    <FormControlLabel 
                      value="open" 
                      control={<Radio />} 
                      label={
                        <Box>
                          <Typography variant="body1">Open to Everyone</Typography>
                          <Typography variant="body2" color="text.secondary">
                            Anyone with the link can access and submit the application.
                          </Typography>
                        </Box>
                      } 
                    />
                    <FormControlLabel 
                      value="invite" 
                      control={<Radio />} 
                      label={
                        <Box>
                          <Typography variant="body1">Invitation Only</Typography>
                          <Typography variant="body2" color="text.secondary">
                            Only invited users can access and submit the application.
                          </Typography>
                        </Box>
                      } 
                    />
                  </RadioGroup>
                </FormControl>
              </CardContent>
            </Card>
            
            {accessType === 'invite' && (
              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">Manage Invitees</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {invitees.length} of {subscriptionLimit} licenses used
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 3 }}>
                    <TextField
                      label="Email"
                      value={newInvitee.email}
                      onChange={(e) => setNewInvitee({...newInvitee, email: e.target.value})}
                      sx={{ mr: 1, flexGrow: 1 }}
                      size="small"
                    />
                    <TextField
                      label="First Name (optional)"
                      value={newInvitee.firstName}
                      onChange={(e) => setNewInvitee({...newInvitee, firstName: e.target.value})}
                      sx={{ mr: 1 }}
                      size="small"
                    />
                    <TextField
                      label="Last Name (optional)"
                      value={newInvitee.lastName}
                      onChange={(e) => setNewInvitee({...newInvitee, lastName: e.target.value})}
                      sx={{ mr: 1 }}
                      size="small"
                    />
                    <Button 
                      variant="contained" 
                      startIcon={<AddIcon />}
                      onClick={handleAddInvitee}
                      disabled={!newInvitee.email || invitees.length >= subscriptionLimit}
                    >
                      Add
                    </Button>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Button
                      component="label"
                      variant="outlined"
                      startIcon={<UploadFileIcon />}
                      sx={{ mr: 2 }}
                    >
                      Upload CSV/XLSX
                      <input
                        type="file"
                        hidden
                        accept=".csv,.xlsx"
                        onChange={handleFileUpload}
                      />
                    </Button>
                    <Typography variant="body2" color="text.secondary">
                      Format: "email", "first_name", "last_name" (headers required)
                    </Typography>
                  </Box>
                  
                  {fileUploadError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {fileUploadError}
                    </Alert>
                  )}

                  {invitees.length > 0 && (
                    <Paper variant="outlined" sx={{ maxHeight: 300, overflow: 'auto' }}>
                      <List dense>
                        {invitees.map((invitee, index) => (
                          <ListItem
                            key={index}
                            secondaryAction={
                              <IconButton edge="end" aria-label="delete" onClick={() => handleRemoveInvitee(index)}>
                                <DeleteIcon />
                              </IconButton>
                            }
                          >
                            <ListItemText
                              primary={invitee.email}
                              secondary={invitee.firstName || invitee.lastName ? `${invitee.firstName || ''} ${invitee.lastName || ''}`.trim() : undefined}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Paper>
                  )}

                  <Alert severity="info" icon={<InfoIcon />} sx={{ mt: 2 }}>
                    You'll be able to add more invitees after launching the application, up to your subscription limit.
                  </Alert>
                </CardContent>
              </Card>
            )}
            
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={handleLaunchClick}
                startIcon={<LaunchIcon />}
              >
                Launch Campaign
              </Button>
            </Box>
          </Box>
        )}

        {/* Confirmation Dialog */}
        <Dialog
          open={confirmDialogOpen}
          onClose={() => setConfirmDialogOpen(false)}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">
            {"Launch Campaign?"}
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              <Typography paragraph>
                Are you sure you want to launch this campaign? Once launched:
              </Typography>
              <ul>
                <li>The application form will be locked and cannot be modified</li>
                <li>The application will be available at: <strong>{applicationUrl}</strong></li>
                {accessType === 'invite' && <li>Only invited users will be able to access the application</li>}
                {accessType === 'open' && <li>Anyone with the link will be able to access the application</li>}
              </ul>
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleConfirmLaunch} variant="contained" autoFocus>
              Launch Campaign
            </Button>
          </DialogActions>
        </Dialog>
      </AccordionDetails>
    </Accordion>
  );
};

export default LaunchPanel;
